package dev.lingvo.site.components.editor

import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderBottom
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.borderTop
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.minWidth
import com.varabyte.kobweb.compose.ui.modifiers.outline
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.position
import com.varabyte.kobweb.compose.ui.modifiers.right
import com.varabyte.kobweb.compose.ui.modifiers.top
import com.varabyte.kobweb.compose.ui.modifiers.bottom
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.modifiers.zIndex
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import dev.lingvo.site.ds.ControlHeight
import dev.lingvo.site.ds.Elevation
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.ScrimVars
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.displayFont
import dev.lingvo.site.ds.elevation
import dev.lingvo.site.ds.noTransition
import dev.lingvo.site.ds.toSitePalette
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px

val EditorUtilityScrimStyle = CssStyle.base {
    Modifier
        .position(Position.Fixed)
        .top(0.px)
        .right(0.px)
        .bottom(0.px)
        .width(100.percent)
        .zIndex(700)
        // The token, not a second copy of its current value. The editor has two sheet scrims and
        // the other one already reads `ScrimVars.Sheet`; a literal here means a light-mode scrim
        // change moves one of them and leaves this one at 54% black.
        .backgroundColor(ScrimVars.Sheet.value())
}

/**
 * The class's own resting state is collapsed, not open.
 *
 * `EditorUtilityPresentation.kt`'s `visibleMotion` overrides `flex`/`width` inline on every render
 * of this element, so in steady state this default never shows through. It is not dead weight: if
 * Compose HTML's DOM patching ever applies a style update by touching the `style` attribute as a
 * whole rather than incrementally, this is what the element falls back to for the instant between
 * the old inline value going away and the new one landing — and a fallback of the *open* width
 * (what this held before) is a fallback that can pre-empt the very entrance transition it belongs
 * to, collapsing "0px → track, animated" into "track → track, nothing to see."
 */
val EditorUtilitySidecarStyle = CssStyle.base {
    Modifier
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .color(colorMode.toSitePalette().textPrimary)
        .borderLeft(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .styleModifier {
            property("box-sizing", "border-box")
            property("display", "flex")
            property("flex", "0 0 0px")
            property("width", "0px")
            property("height", "100%")
            property("min-width", "0")
            property("min-height", "0")
            property("overflow", "hidden")
        }
}

val EditorUtilityMeasuringStyle = CssStyle.base {
    Modifier
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .color(colorMode.toSitePalette().textPrimary)
        .borderLeft(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .styleModifier {
            property("box-sizing", "border-box")
            property("display", "flex")
            property("flex", "0 0 $EDITOR_UTILITY_TRACK")
            property("width", EDITOR_UTILITY_TRACK)
            property("height", "100%")
            property("min-width", "0")
            property("min-height", "0")
            property("overflow", "hidden")
            property("visibility", "hidden")
            property("pointer-events", "none")
        }
}

val EditorUtilitySheetStyle = CssStyle.base {
    Modifier
        .position(Position.Fixed)
        .right(0.px)
        .bottom(0.px)
        .width(100.percent)
        .minHeight(0.px)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .color(colorMode.toSitePalette().textPrimary)
        .borderTop(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
        .borderRadius(topLeft = Radius.Modal, topRight = Radius.Modal)
        .zIndex(701)
        // The upward shadow this surface used to spell out by hand. `Elevation.Sheet` carries that
        // exact dark value and a lighter one for paper, so the sheet stops being the one surface in
        // the product that writes its own `box-shadow`.
        .elevation(Elevation.Sheet)
        .styleModifier {
            property("box-sizing", "border-box")
            property("display", "flex")
            property("flex-direction", "column")
            property("max-height", "min(82dvh, calc(100dvh - ${EDITOR_HEADER_HEIGHT_PX}px))")
            property("overflow", "hidden")
            property("transform-origin", "center bottom")
        }
}

val EditorUtilityPaneStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minHeight(0.px)
        .styleModifier {
            property("display", "flex")
            property("flex-direction", "column")
            property("flex", "1 1 auto")
            property("height", "100%")
            property("min-width", "0")
            property("min-height", "0")
        }
}

val EditorUtilityHeaderStyle = CssStyle.base {
    Modifier
        .position(Position.Sticky)
        .top(0.px)
        .zIndex(2)
        .fillMaxWidth()
        .minWidth(0.px)
        .gap(Space.SM)
        .padding(leftRight = Space.LG)
        .backgroundColor(colorMode.toSitePalette().surfaceRaised)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier {
            property("box-sizing", "border-box")
            property("min-height", "52px")
        }
}

val EditorUtilityTabListStyle = CssStyle.base {
    Modifier
        .minWidth(0.px)
        // `MD` between tabs, `SM` inside one (EditorUtilityTabStyle): the space *between* two tabs
        // still reads as larger than the space between a glyph and the word it belongs to, which
        // is the only relationship this pair of numbers has to get right. It came down from `LG`
        // when the glyph gap went up: four tabs in a 400px pane then measured 338 against 328 of
        // room, and "Details" scrolled out of sight under the close button.
        .gap(Space.MD)
        .styleModifier {
            property("display", "flex")
            property("flex", "1 1 auto")
            property("overflow-x", "auto")
            property("overflow-y", "hidden")
            property("overscroll-behavior-inline", "contain")
            property("scrollbar-width", "thin")
        }
}

val EditorUtilityTabStyle = CssStyle {
    base {
        Modifier
            .minHeight(ControlHeight.MD)
            .backgroundColor(com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .color(colorMode.toSitePalette().textSecondary)
            .border(0.px, LineStyle.Solid, com.varabyte.kobweb.compose.ui.graphics.Colors.Transparent)
            .padding(topBottom = Space.MD)
            .fontSize(TypeScale.BodySmall)
            .fontWeight(600)
            // 8px, the same distance `LButton` puts between a leading glyph and its label
            // (LButton.kt). At `XS` the wand overlapped the A of "Assist" and the info circle
            // abutted the D of "Details": 4px is below what these glyphs' own bounding boxes need,
            // and it made the tab strip the tightest icon/label pairing in the product.
            .gap(Space.SM)
            .styleModifier {
                property("box-sizing", "border-box")
                property("cursor", "pointer")
                // The tab holds a glyph, its word and — on Discuss — an attention dot. Baseline
                // rather than centre alignment would drop the icon a pixel below the word at this
                // size; the row is one line high and centring is what keeps the three on it.
                property("display", "inline-flex")
                property("align-items", "center")
                property("flex", "0 0 auto")
                property("margin-bottom", "-1px")
                property("white-space", "nowrap")
            }
            .noTransition()
    }
    cssRule("[aria-selected=\"true\"]") {
        Modifier
            .color(colorMode.toSitePalette().accent)
            .borderBottom(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
    }
    focusVisible {
        Modifier
            .outline(2.px, LineStyle.Solid, colorMode.toSitePalette().accent)
            .styleModifier { property("outline-offset", "2px") }
    }
}

/**
 * The Discuss tab's attention dot: a question is open on this key.
 *
 * A dot rather than a count badge, because the number is not the point — "somebody asked something
 * here" is a binary a translator acts on, and a badge reading "1" beside a word reads as a quantity
 * of tabs. The count is still said, in the visually-hidden sentence beside it, so the dot is never
 * the only carrier of the fact.
 */
val EditorUtilityTabDotStyle = CssStyle.base {
    Modifier
        .width(6.px)
        .height(6.px)
        .borderRadius(50.percent)
        .backgroundColor(colorMode.toSitePalette().warning)
        .styleModifier { property("flex", "0 0 auto") }
}

val EditorUtilityTabPanelStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minWidth(0.px)
        .minHeight(0.px)
        .styleModifier {
            property("display", "flex")
            property("flex", "1 1 auto")
            property("flex-direction", "column")
            property("overflow", "hidden")
        }
}

val EditorUtilityBodyStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .flexGrow(1)
        .minWidth(0.px)
        .minHeight(0.px)
        .gap(Space.LG)
        .padding(top = Space.LG, bottom = Space.XXL, leftRight = Space.LG)
        .styleModifier {
            property("box-sizing", "border-box")
            property("flex", "1 1 auto")
            property("min-height", EditorCompactSummaryBounds.bodyMinHeight)
            property("overflow-y", "auto")
            property("overflow-x", "hidden")
            property("overscroll-behavior", "contain")
            property("scrollbar-gutter", "stable")
        }
}

val EditorUtilityPanelStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minHeight(0.px)
        .gap(Space.MD)
        .styleModifier {
            property("overflow-wrap", "anywhere")
        }
}

val EditorUtilityEmptyStyle = CssStyle.base {
    Modifier
        .fillMaxWidth()
        .minHeight(0.px)
        .padding(topBottom = Space.LG, leftRight = Space.MD)
        .gap(Space.XS)
        .color(colorMode.toSitePalette().textSecondary)
        .styleModifier {
            property("box-sizing", "border-box")
            property("overflow-wrap", "anywhere")
        }
}

val EditorUtilityEmptyTitleStyle = CssStyle.base {
    Modifier
        .displayFont()
        // BodySmall, the level of the pane's own section headings ("Suggestions", "Relevant
        // glossary"). It used to be HeadingSmall on the argument that an empty state is the same
        // moment wherever it appears — but LEmptyState's H3 is a full-page moment, and inside a
        // 400px side pane that made "No suggestions yet" outrank the heading of the section it is
        // nested in and tie with Source/Target for the largest type on screen. An absence never
        // outranks the thing it is absent from. The bold stays; only the size comes down.
        .fontSize(TypeScale.BodySmall)
        .fontWeight(700)
        .color(colorMode.toSitePalette().textPrimary)
}

val EditorCompactSummaryStyle = CssStyle.base {
    Modifier
        .padding(leftRight = Space.LG, topBottom = Space.MD)
        .gap(Space.SM)
        .backgroundColor(colorMode.toSitePalette().surfaceSunken)
        .borderBottom(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
        .styleModifier {
            property("box-sizing", "border-box")
            property("max-height", EditorCompactSummaryBounds.summaryMaxHeight)
            property("flex", "0 0 auto")
            property("overflow-wrap", "anywhere")
        }
}

val EditorCompactSummaryValuesStyle = CssStyle.base {
    Modifier
        .gap(Space.XS)
        .styleModifier {
            property("min-height", "0")
            property("overflow-y", "auto")
            property("overflow-wrap", "anywhere")
            property("user-select", "text")
        }
}

val EditorCompactSummaryCategoryStyle = CssStyle.base {
    Modifier
        .fontSize(TypeScale.Micro)
        .fontWeight(700)
        .color(colorMode.toSitePalette().textSecondary)
        .styleModifier {
            property("text-transform", "uppercase")
            property("letter-spacing", "0")
        }
}

val EditorUtilityCommentComposerStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .gap(Space.SM)
            .styleModifier {
                property("min-width", "0")
            }
    }
    cssRule(" textarea") {
        Modifier
            .backgroundColor(colorMode.toSitePalette().surfaceSunken)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().textMuted)
    }
}
